import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ArrowLeftRight, ArrowDown, ArrowUp, RotateCcw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DateInput from "@/components/DateInput";
import ProjectSelect from "@/components/ProjectSelect";
import { fmtDate, todayISO } from "@/lib/utils";

const EMPTY = { project_id: "", movement_date: todayISO(), material_name: "", unit: "adet", quantity: 0, movement_type: "giris", source: "", destination: "", notes: "" };
const UNITS = ["m", "m2", "m3", "adet", "kg", "ton", "lt", "takım"];

export default function MaterialMovements() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/material-movements${filter ? `?project_id=${filter}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.project_id || !form.material_name) { toast.error("Proje ve malzeme zorunlu"); return; }
    await api.post("/material-movements", { ...form, quantity: Number(form.quantity) });
    toast.success("Hareket kaydedildi"); setOpen(false); setForm(EMPTY); load();
  };
  const remove = async (id) => { await api.delete(`/material-movements/${id}`); toast.success("Silindi"); load(); };

  const typeIcon = { giris: ArrowDown, cikis: ArrowUp, iade: RotateCcw };
  const typeColor = { giris: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/15", cikis: "text-rose-600 dark:text-rose-400 bg-rose-500/15", iade: "text-blue-600 dark:text-blue-400 bg-blue-500/15" };
  const typeLabel = { giris: "Giriş", cikis: "Çıkış", iade: "İade" };

  return (
    <div data-testid="material-movements-page">
      <PageHeader title="Malzeme Hareketleri" subtitle="Giriş, çıkış ve iade stok hareketleri.">
        <ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-mm-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni Hareket</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Yeni Malzeme Hareketi</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" /></div>
              <div><Label>Tarih</Label><DateInput data-testid="input-movement-date" value={form.movement_date} onChange={e => setForm({...form, movement_date: e.target.value})} /></div>
              <div><Label>Hareket Tipi</Label><Select value={form.movement_type} onValueChange={v => setForm({...form, movement_type: v})}><SelectTrigger data-testid="input-movement-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="giris">Giriş</SelectItem><SelectItem value="cikis">Çıkış</SelectItem><SelectItem value="iade">İade</SelectItem></SelectContent></Select></div>
              <div className="col-span-2"><Label>Malzeme *</Label><Input data-testid="input-material" value={form.material_name} onChange={e => setForm({...form, material_name: e.target.value})} /></div>
              <div><Label>Birim</Label><Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Miktar</Label><Input type="number" step="0.01" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} /></div>
              <div><Label>Kaynak</Label><Input value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="Tedarikçi / Depo" /></div>
              <div><Label>Hedef</Label><Input value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} placeholder="Şantiye / Depo" /></div>
              <div className="col-span-2"><Label>Notlar</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-mm-button">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <ArrowLeftRight className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz hareket kaydı yok</div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>Tarih</TableHead><TableHead>Tip</TableHead><TableHead>Malzeme</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Kaynak</TableHead><TableHead>Hedef</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map(r => {
                const Icon = typeIcon[r.movement_type];
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono-nums">{fmtDate(r.movement_date)}</TableCell>
                    <TableCell><Badge className={typeColor[r.movement_type]}><Icon className="w-3 h-3 mr-1 inline" />{typeLabel[r.movement_type]}</Badge></TableCell>
                    <TableCell className="font-medium">{r.material_name}</TableCell>
                    <TableCell className="text-right font-mono-nums">{r.quantity} {r.unit}</TableCell>
                    <TableCell className="text-muted-foreground">{r.source || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{r.destination || "-"}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
